import React, { useState, useEffect } from 'react';
import { Card, Badge, Spin } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';


const PaymentMethodSelection = ({ onMethodSelect, selectedMethod, onWithdrawClick, paymentMethod }) => {
    const [paymentGateways, setPaymentGateways] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Processing time color mapping
    const getProcessingTimeColor = (processingTime) => {
        if (processingTime?.toLowerCase().includes('instant') || processingTime?.toLowerCase().includes('immediate')) {
            return 'green';
        } else if (processingTime?.toLowerCase().includes('hour') || processingTime?.toLowerCase().includes('1-2')) {
            return 'blue';
        } else {
            return 'yellow';
        }
    };

    // Static withdraw payment methods (limited to 3: BankTransfer, Paypal, Crypto)
    const withdrawMethods = [
        {
            id: 1,
            name: "BankTransfer",
            title: "Bank Transfer",
            description: "Secure bank transfer withdrawal",
            value: 1,
            fees: 0,
            processingTime: "24 Hours",
            processingTimeColor: getProcessingTimeColor("1-2 Hours"),
            fee: "$ 0",
            minAmount: `$ ${paymentMethod?.minAmount}`,
            maxAmount: `$ ${paymentMethod?.maxAmount}`,
            paymentLogoUrl: "/assets/images/bank.png",
            icon: <img src="/assets/images/bank.png" alt="Bank Transfer" className="w-8 h-8" />,
            iconBg: 'bg-blue-100',
            iconColor: 'blue',
            buttonText: "Withdraw via Bank Transfer",
            buttonColor: 'bg-gradient'
        },
        {
            id: 3,
            name: "Paypal",
            title: "PayPal",
            description: "Withdraw via PayPal",
            value: 3,
            fees: 2.5,
            processingTime: "Instant",
            processingTimeColor: getProcessingTimeColor("Instant"),
            fee: "$ 0",
            minAmount: `$ ${paymentMethod?.minAmount ?? 1}`,
            maxAmount: `$ ${paymentMethod?.maxAmount ?? 10000}`,
            paymentLogoUrl: "/assets/images/paypal.png",
            icon: <img src="/assets/images/paypal.png" alt="PayPal" className="w-8 h-8" />,
            iconBg: 'bg-yellow-100',
            iconColor: 'yellow',
            buttonText: "Withdraw via PayPal",
            buttonColor: 'bg-gradient'
        },
        {
            id: 4,
            name: "Crypto",
            title: "Crypto Withdrawal",
            description: "Withdraw using supported cryptocurrencies",
            value: 4,
            fees: 0.0,
            processingTime: "1-2 Hours",
            processingTimeColor: getProcessingTimeColor("1-2 Hours"),
            fee: "$ 0",
            minAmount: `$ ${paymentMethod?.minAmount ?? 10}`,
            maxAmount: `$ ${paymentMethod?.maxAmount ?? 50000}`,
            paymentLogoUrl: "/assets/images/crypto.png",
            icon: <img src="/assets/images/crypto.png" alt="Crypto" className="w-8 h-8" />,
            iconBg: 'bg-purple-100',
            iconColor: 'purple',
            buttonText: "Withdraw via Crypto",
            buttonColor: 'bg-gradient'
        }
        // Other methods intentionally commented out to limit to 3 options
        // {
        //     name: "Cash",
        //     value: 6,
        //     fees: 0,
        //     processingTime: "1 H",
        //     paymentLogoUrl: "/assets/images/bank.png"
        // },
        // {
        //     name: "UPI",
        //     value: 2,
        //     fees: 0,
        //     processingTime: "1 H",
        //     paymentLogoUrl: "/assets/images/upi.png"
        // }
    ];

    useEffect(() => {
        setIsLoading(true);
        // Simulate loading for consistency with deposit component
        setTimeout(() => {
            setPaymentGateways(withdrawMethods);
            setIsLoading(false);
        }, 500);
    }, []);
    useEffect(() => {
        if (!isLoading && paymentGateways.length > 0 && !selectedMethod) {
            onMethodSelect && onMethodSelect(paymentGateways[0]);
        }
    }, [isLoading, paymentGateways, selectedMethod, onMethodSelect]);

    const getProcessingTimeBadgeColor = (color) => {
        switch (color) {
            case 'green': return 'success';
            case 'yellow': return 'warning';
            case 'blue': return 'processing';
            default: return 'default';
        }
    };

    const handleMethodClick = (method) => {
        onMethodSelect && onMethodSelect(method);
    };

    return (
        <div className="w-full p-6 border-2 border-light rounded-md mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className='w-full flex justify-between'>
                    <h1 className="text-xl font-semibold text-gray-800">Select Withdrawal Method</h1>
                    <div className="flex items-center text-gray-600">
                        <InfoCircleOutlined className="mr-2" />
                        <span>All withdrawals are processed securely</span>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <Spin size="large" />
                </div>
            ) : paymentGateways.length === 0 ? (
                <div className="flex justify-center items-center py-20">
                    <div className="text-center">
                        <InfoCircleOutlined className="text-4xl text-gray-400 mb-4" />
                        <p className="text-gray-600">No withdrawal methods available at the moment.</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Payment Methods Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {paymentGateways.map((method) => (
                            <Card
                                key={method.id}
                                className={`relative cursor-pointer border-2 ${selectedMethod?.id === method.id ? 'border-primary' : 'border-light'
                                    }`}
                                onClick={() => handleMethodClick(method)}
                            >
                                {/* Processing Time Badge */}
                                <div className="absolute top-4 right-4">
                                    <Badge
                                        status={getProcessingTimeBadgeColor(method.processingTimeColor)}
                                        text={method.processingTime}
                                        className="text-xs"
                                    />
                                </div>

                                <div className="p-6">
                                    {/* Icon and Title */}
                                    <div className="flex items-center mb-6">
                                        <div className={`${method.iconBg} ${method.iconColor} w-10 h-10 rounded-xl flex items-center justify-center mr-4`}>
                                            {method.icon}
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900">{method.title}</h3>
                                            <p className="text-gray-600 text-xs">{method.description}</p>
                                        </div>
                                    </div>

                                    {/* Payment Details */}
                                    <div className="space-y-4 mb-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600 text-sm">Processing Time:</span>
                                            <span className="font-medium text-gray-900">{method.processingTime}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600 text-sm">Fee:</span>
                                            <span className="font-medium text-gray-900">{method.fee}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600 text-sm">Min/Max:</span>
                                            <span className="font-medium text-gray-900">{method.minAmount} - {method.maxAmount}</span>
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <button
                                        className={`${method.buttonColor} border-none text-white font-medium h-10 rounded-lg w-full`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMethodClick(method);
                                            onWithdrawClick && onWithdrawClick();
                                        }}
                                    >
                                        {method.buttonText}
                                    </button>
                                </div>

                                {/* Selected Indicator */}
                                {selectedMethod?.id === method.id && (
                                    <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
                                )}
                            </Card>
                        ))}
                    </div>

                    {/* Footer Info */}
                    <div className="mt-3 p-4">
                        <div className="flex items-center justify-center text-gray-600 text-sm">
                            <InfoCircleOutlined className="mr-2" />
                            <span>All withdrawal methods are secured with industry-standard encryption. Processing times may vary based on your bank or payment provider.</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PaymentMethodSelection;